import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Define the props for our Quill editor
interface QuillEditorProps {
  value: string;
  onChange: (content: string,  source?: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  theme?: string;
  modules?: any;
}

// Define the ref interface with proper types
export interface QuillEditorRef {
  getEditor: () => any;
  focus: () => void;
  blur: () => void;
}

/**
 * A wrapper around ReactQuill with custom configuration
 */
const QuillEditor = forwardRef<QuillEditorRef, QuillEditorProps>(
  ({ value, onChange, readOnly = false, placeholder, theme = 'snow', modules }, ref) => {
    const reactQuillRef = useRef<ReactQuill>(null);
    
    // Default modules configuration if not provided
    const defaultModules = {
      toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
    };
    
    // Handle content change without cleaning (cleaning at save time only to avoid cursor reset)
    const handleChange = (
      content: string,
      _delta: unknown,
      source: string,
      _editor: unknown
    ) => {
      onChange(content, source);
    };
    
    // Forward the ref methods we want to expose
    useImperativeHandle(ref, () => ({
      getEditor: () => reactQuillRef.current?.getEditor(),
      focus: () => reactQuillRef.current?.focus(),
      blur: () => reactQuillRef.current?.blur()
    }));
    
    return (
      <ReactQuill
        ref={reactQuillRef}
        theme={theme}
        value={value}
        onChange={handleChange}
        readOnly={readOnly}
        placeholder={placeholder}
        modules={modules || defaultModules}
        style={{
          backgroundColor: readOnly ? '#f5f5f5' : 'white'
        }}
      />
    );
  }
);

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;